// @vendors
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @icons
import { Bars3Icon } from "@heroicons/react/24/outline";

// @store
import useAuthStore from "../../store/useAuthStore";

// @components
import LoginButton from "../../components/molecules/auth/LoginButton";
import { LanguageToggle } from "../../components/atoms/LanguageToggle";
import ThemeToggle from "../../components/atoms/ThemeToggle";
import routesConfig from "../../config/routes.config";

// Component: Header
const Header: React.FC = () => {
  const { t } = useTranslation();
  const isLoggedIn = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  //const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  // Toggle menu visibility
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Toggle language menu visibility
  /* const toggleLanguageMenu = () => {
    setLanguageMenuOpen(!languageMenuOpen);
  }; */

  // Get link class based on the current path
  const getLinkClass = (path: string) => {
    return location.pathname === path
      ? "text-textBrand dark:text-dark-textBrand"
      : "text-cText dark:text-dark-cText";
  };

  // NavLink component
  const NavLink = ({ href, children, disabled }: { href: string; children: React.ReactNode; disabled?: boolean }) => (
    <a
      href={disabled ? undefined : href}
      className={`${getLinkClass(href)} ${disabled ? "text-cTextSecondary dark:text-dark-cTextSecondary pointer-events-none" : ""} ${isLoggedIn || href === "/" || href === "/marketplace" ? "" : "text-outline pointer-events-none"}`}
    >
      {children}
    </a>
  );

  // Routes for navigation
  const routes = [
    { path: routesConfig.home, label: t("navbar.home") },
    { path: routesConfig.myServices, label: t("navbar.myServices"), requiresAuth: true },
    { path: routesConfig.myRequests, label: t("navbar.myRequests"), requiresAuth: true },
    { path: routesConfig.marketplaceUser, label: t("navbar.marketplace") },
    { path: routesConfig.userProfile, label: t("navbar.myProfile"), requiresAuth: true },
  ];

  return (
    <nav className="flex py-4 md:py-6 md:px-4 3xl:container 3xl:mx-auto">
      <div className="flex flex-wrap items-center justify-between w-full">
        {/* Logo */}
        <a href="/" className="flex items-center space-x-3 rtl:space-x-reverse">
          <span className="pl-6 xl:pl-4 text-textBrand text-center font-serif dark:text-dark-bgBrand text-[22px] xl:text-3xl font-bold leading-10 tracking-[5.50px] self-center whitespace-nowrap">
            XERVIX
          </span>
        </a>

        {/* Navigation links */}
        <ul className="hidden text-cText dark:text-dark-cText font-medium xl:flex xl:gap-10 text-[15px] ">
          {routes.map((route) => (
            <li key={route.path}>
              <NavLink
                href={route.path}
                disabled={route.requiresAuth && !isLoggedIn}
              >
                {route.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Theme and language toggles */}
        <div className="flex items-center pr-2 relative">

          {/* Theme toggle button */}
          <ThemeToggle />

          {/* Language toggle button */}
          <LanguageToggle />

          {/* Login section */}
          <div className="hidden xl:flex">
            <LoginButton />
          </div>

          {/* Hamburger menu button */}
          <button
            onClick={toggleMenu}
            className="inline-flex items-center justify-center xl:hidden size-10 text-sm text-cText rounded-lg hover:bg-cBackgroundSecondary focus:bg-cBackgroundSecondary dark:text-dark-cText dark:hover:bg-dark-cBackgroundSecondary dark:focus:bg-dark-cBackgroundSecondary"
            aria-expanded={menuOpen}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="size-6 text-cText dark:text-dark-cText" />
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`${menuOpen ? "block" : "hidden"} w-full mt-4 md:mt-0  bg-textBrand dark:bg-textBrand py-2`}
          id="navbar-hamburger"
        >
          <ul className="flex flex-col font-medium space-y-2">
            {routes.map((route) => (
              <li key={route.path}>
                <a
                  href={route.path}
                  className="text-[14px] block py-1 px-6 text-dark-cText dark:text-dark-cText rounded hover:bg-bgBrand hover:text-white"
                >
                  {route.label}
                </a>
              </li>
            ))}
            <li>
              <div
                //href="#"
                className="text-[14px] block px-6 py-1 text-dark-cText dark:text-dark-cText rounded hover:bg-bgBrand hover:text-dark-cText :bg-white"
              >
                <LoginButton />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
